/**
 * One row in the Areas or Projects table, and the add-row form beneath it.
 *
 * Shared between `AreasCard` and `ProjectsCard` — same row shape, same edit/delete/status
 * dance, parameterised by `kind` where the two containers genuinely differ (a project can
 * be completed and archived separately; an area only archives, since §4.1 gives it no end).
 */

import { useState } from "react";
import { deleteProjectQuestion } from "../deleteProject";
import type { Area, Project } from "../api";
import { TaskDrawer } from "./TaskDrawer";
import { EyeToggle } from "./EyeToggle";
import type { TaskActions } from "./TaskList";
import { Pencil } from "./icons";

export const DEFAULT_THRESHOLD = 14;

export type SortKey = "name" | "area";

export function AddRow({
  label,
  areas,
  onAdd,
}: {
  label: string;
  areas?: Area[];
  onAdd: (name: string, threshold: number, areaId?: string) => void;
}) {
  const [name, setName] = useState("");
  const [threshold, setThreshold] = useState(String(DEFAULT_THRESHOLD));
  const [areaId, setAreaId] = useState("");

  const ready = name.trim() && (!areas || areaId);

  return (
    <form
      className="add-row"
      onSubmit={(e) => {
        e.preventDefault();
        if (!ready) return;
        onAdd(name.trim(), Number(threshold) || DEFAULT_THRESHOLD, areaId || undefined);
        setName("");
      }}
    >
      <input
        className="input add-name"
        placeholder={`New ${label}`}
        aria-label={`New ${label} name`}
        value={name}
        onChange={(e) => setName(e.target.value)}
      />

      {areas && (
        <select
          className="input add-area"
          aria-label="Area"
          value={areaId}
          onChange={(e) => setAreaId(e.target.value)}
        >
          <option value="">choose an area…</option>
          {areas.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </select>
      )}

      {/*
        The threshold and the button travel together. Left as four peers they wrapped in
        source order, so the button was the first thing to fall onto its own line and landed
        left-justified under a half-empty row.
      */}
      <div className="add-tail">
        <label className="small muted row add-quiet">
          quiet after
          <input
            className="input"
            type="number"
            min={1}
            style={{ width: "4.5rem" }}
            aria-label={`Quiet after days for the new ${label}`}
            value={threshold}
            onChange={(e) => setThreshold(e.target.value)}
          />
          days
        </label>

        <button className="btn primary" type="submit" disabled={!ready}>
          Add {label}
        </button>
      </div>
    </form>
  );
}

export function SortHeader({
  label,
  column,
  sort,
  onSort,
}: {
  label: string;
  column: SortKey;
  sort: { key: SortKey; desc: boolean };
  onSort: (key: SortKey) => void;
}) {
  const active = sort.key === column;
  return (
    <th
      role="columnheader"
      aria-sort={active ? (sort.desc ? "descending" : "ascending") : "none"}
    >
      <button
        className="btn ghost small sort-header"
        onClick={() => onSort(column)}
        title={
          active
            ? `Sorted by ${label.toLowerCase()}, ${sort.desc ? "Z to A" : "A to Z"} - click to reverse`
            : `Sort by ${label.toLowerCase()}`
        }
      >
        {label}
        <span className={`sort-mark${active ? " on" : ""}`} aria-hidden="true">
          {active && sort.desc ? "▼" : "▲"}
        </span>
      </button>
    </th>
  );
}

export function StructureRow({
  id,
  kind,
  name,
  threshold,
  context,
  areaId,
  taskCount,
  archived,
  done = false,
  onComplete,
  statusLabel,
  statusHint,
  extraAction,
  onSave,
  onStatus,
  onDelete,
  today,
  allAreas,
  allProjects,
  actions,
  drawerRefreshKey,
  onDrawerChange,
  index,
  draggable = false,
  gripProps,
  rowProps,
}: {
  id: string;
  kind: "area" | "project";
  name: string;
  threshold: number;
  context?: string;
  /** A project's area, so editing it can offer to move it (its tasks and notes go with it). */
  areaId?: string;
  /** Open tasks filed under this area/project — undefined skips the `· N` suffix rather
   * than showing a misleading zero. */
  taskCount?: number;
  archived: boolean;
  done?: boolean;
  /** Present when this can be finished — projects only. Areas never end (§4.1). */
  onComplete?: () => void;
  statusLabel?: string;
  statusHint: string;
  extraAction?: { label: string; hint: string; run: () => void };
  onSave: (patch: Record<string, unknown>) => void;
  onStatus: () => void;
  onDelete: () => void;
  today: string;
  allAreas: Area[];
  allProjects: Project[];
  actions: TaskActions;
  /** Bumped whenever the card around this row reloads, so an open drawer refetches rather
   * than showing a list that was true a minute ago. */
  drawerRefreshKey?: number;
  /** Called after the drawer changes anything, so the row's own `· N` count and any sibling
   * card relying on the same tasks know to refetch too. */
  onDrawerChange?: () => void | Promise<void>;
  index?: number;
  draggable?: boolean;
  /** Supplied by `useRowReorder` when this list can be reordered. */
  gripProps?: (index: number) => Record<string, unknown>;
  rowProps?: (index: number) => Record<string, unknown>;
}) {
  const [editing, setEditing] = useState(false);
  const [open, setOpen] = useState(false);
  const [draftName, setDraftName] = useState(name);
  const [draftThreshold, setDraftThreshold] = useState(String(threshold));
  const [draftArea, setDraftArea] = useState(areaId ?? "");

  const columns = 4;

  if (editing) {
    return (
      <tr role="row">
        <td role="cell" className="cell-name">
          <input
            className="input"
            aria-label="Name"
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            autoFocus
          />
        </td>
        <td role="cell" className="cell-quiet">
          <input
            className="input"
            type="number"
            min={1}
            style={{ width: "5rem" }}
            aria-label="Quiet after days"
            value={draftThreshold}
            onChange={(e) => setDraftThreshold(e.target.value)}
          />
        </td>
        <td role="cell" className="small muted cell-context">
          {kind === "project" && areaId ? (
            <select
              className="input"
              aria-label="Area"
              value={draftArea}
              onChange={(e) => setDraftArea(e.target.value)}
            >
              {allAreas.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          ) : (
            context
          )}
          {kind === "project" && areaId && draftArea !== areaId && (
            <span className="small muted"> Its tasks and notes move with it.</span>
          )}
        </td>
        <td role="cell" className="cell-actions">
          <span className="row">
            {/*
              Delete lives behind Edit on purpose: it is two deliberate clicks from the
              resting state, and it sits next to the fields rather than next to the
              everyday controls it would otherwise be a slip away from.
            */}
            <button
              className="btn ghost small danger"
              title={`Permanently delete this ${kind}. Only possible while it is empty.`}
              onClick={() => {
                if (
                  window.confirm(
                    kind === "project"
                      ? deleteProjectQuestion(name)
                      : `Permanently delete the ${kind} "${name}"?\n\n` +
                          "This cannot be undone, and only works if it is empty. To retire one " +
                          "that still holds work, archive it instead - that keeps everything.",
                  )
                ) {
                  onDelete();
                  setEditing(false);
                }
              }}
            >
              Delete
            </button>
            <span style={{ flex: 1 }} />
            <button
              className="btn primary small"
              onClick={() => {
                const patch: Record<string, unknown> = {};
                if (draftName.trim() && draftName.trim() !== name)
                  patch.name = draftName.trim();
                if (Number(draftThreshold) !== threshold) {
                  patch.stale_after_days = Number(draftThreshold);
                }
                if (areaId && draftArea && draftArea !== areaId) patch.area_id = draftArea;
                if (Object.keys(patch).length > 0) onSave(patch);
                setEditing(false);
              }}
            >
              Save
            </button>
            <button className="btn ghost small" onClick={() => setEditing(false)}>
              Cancel
            </button>
          </span>
        </td>
      </tr>
    );
  }

  // Called once: it carries both the hit-test attributes and the drop-line class, and the
  // explicit `className` below has to win over the spread.
  const drag = draggable && index !== undefined ? rowProps?.(index) : undefined;

  return (
    <>
      {/*
        Drop target and drag state both come from `useRowReorder`, which hit-tests with
        pointer events rather than HTML5 drag-and-drop - the latter never fired on touch.
      */}
      <tr
        role="row"
        {...drag}
        className={[archived ? "revoked" : "", drag?.className ?? ""]
          .filter(Boolean)
          .join(" ")}
      >
        <td role="cell" className="cell-name">
          <span className="row" style={{ gap: "0.5rem" }}>
            {onComplete && (
              <button
                className="tick"
                onClick={onComplete}
                aria-label={done ? `Reopen: ${name}` : `Complete: ${name}`}
                title={
                  done
                    ? "Done, and in your wins. Click to reopen."
                    : "Finished - reached its outcome. This is a win."
                }
              >
                {done ? "✓" : ""}
              </button>
            )}
            <span>
              {name}
              {taskCount !== undefined && <span className="muted"> · {taskCount}</span>}
            </span>
          </span>
        </td>
        <td role="cell" className="small cell-quiet">
          {threshold} days
        </td>
        <td role="cell" className="small muted cell-context">
          {context}
        </td>
        <td role="cell" className="cell-actions">
          <span className="row">
            <EyeToggle
              open={open}
              onToggle={() => setOpen((v) => !v)}
              label={`tasks in ${name}`}
            />
            <button className="btn ghost small" onClick={() => setEditing(true)} title="Edit">
              <Pencil />
            </button>
            {statusLabel && (
              <button className="btn ghost small" onClick={onStatus} title={statusHint}>
                {statusLabel}
              </button>
            )}
            {extraAction && (
              <button
                className="btn ghost small"
                onClick={extraAction.run}
                title={extraAction.hint}
              >
                {extraAction.label}
              </button>
            )}
            {draggable && (
              <span
                className="grip"
                role="button"
                tabIndex={0}
                {...(index !== undefined ? gripProps?.(index) : {})}
              >
                ⠿
              </span>
            )}
          </span>
        </td>
      </tr>

      {open && (
        <tr role="row" className="drawer-row">
          <td role="cell" colSpan={columns}>
            <TaskDrawer
              kind={kind}
              containerId={id}
              containerName={name}
              today={today}
              areas={allAreas}
              projects={allProjects}
              actions={actions}
              refreshKey={drawerRefreshKey}
              onChanged={onDrawerChange}
            />
          </td>
        </tr>
      )}
    </>
  );
}
